import { useState, useEffect, useRef } from 'react';

export interface WMSLayerData {
  yield: string[];
  vhi: string[];
  lst: string[];
}

export interface WMSLayerState {
  yield: {
    visible: boolean;
    currentDate: string;
    isPlaying: boolean;
    currentIndex: number;
  };
  vhi: {
    visible: boolean;
    currentDate: string;
    isPlaying: boolean;
    currentIndex: number;
  };
  lst: {
    visible: boolean;
    currentDate: string;
    isPlaying: boolean;
    currentIndex: number;
  };
}

export interface UseWMSLayersProps {
  dateArrays: WMSLayerData;
  onDateChange?: (date: string, layerType: 'yield' | 'vhi' | 'lst') => void;
  playbackSpeed?: number;
}

export function useWMSLayers({
  dateArrays,
  onDateChange,
  playbackSpeed = 1000
}: UseWMSLayersProps) {
  const [layerStates, setLayerStates] = useState<WMSLayerState>({
    yield: {
      visible: false,
      currentDate: dateArrays.yield[0] || '',
      isPlaying: false,
      currentIndex: 0,
    },
    vhi: {
      visible: false,
      currentDate: dateArrays.vhi[0] || '',
      isPlaying: false,
      currentIndex: 0,
    },
    lst: {
      visible: false,
      currentDate: dateArrays.lst[0] || '',
      isPlaying: false,
      currentIndex: 0,
    },
  });

  // Refs for intervals
  const intervalsRef = useRef<{
    yield?: NodeJS.Timeout;
    vhi?: NodeJS.Timeout;
    lst?: NodeJS.Timeout;
  }>({});

  // Reset layer state to first date and stop playback
  const resetLayerState = (layerType: 'yield' | 'vhi' | 'lst') => {
    const dates = dateArrays[layerType];
    const firstDate = dates && dates.length > 0 ? dates[0] : '';
    
    // Stop playback if running
    if (intervalsRef.current[layerType]) {
      clearInterval(intervalsRef.current[layerType]);
      intervalsRef.current[layerType] = undefined;
    }
    
    // Reset to first date
    setLayerStates(prev => ({
      ...prev,
      [layerType]: {
        ...prev[layerType],
        currentDate: firstDate,
        currentIndex: 0,
        isPlaying: false,
        visible: false,
      }
    }));
    
    // Update the map layer if date change callback exists
    if (onDateChange && firstDate) {
      onDateChange(firstDate, layerType);
    }
  };

  // Toggle layer visibility
  const toggleLayerVisibility = (layerType: 'yield' | 'vhi' | 'lst') => {
    setLayerStates(prev => ({
      ...prev,
      [layerType]: {
        ...prev[layerType],
        visible: !prev[layerType].visible,
        isPlaying: false, // Stop playing when toggling visibility
      }
    }));

    // Clear interval if hiding
    if (layerStates[layerType].visible && intervalsRef.current[layerType]) {
      clearInterval(intervalsRef.current[layerType]);
      intervalsRef.current[layerType] = undefined;
    }
  };

  // Set layer date by index
  const setLayerDateByIndex = (layerType: 'yield' | 'vhi' | 'lst', index: number) => {
    const dates = dateArrays[layerType];
    if (dates && dates[index]) {
      const newDate = dates[index];
      setLayerStates(prev => ({
        ...prev,
        [layerType]: {
          ...prev[layerType],
          currentDate: newDate,
          currentIndex: index,
        }
      }));

      if (onDateChange) {
        onDateChange(newDate, layerType);
      }
    }
  };

  // Set layer date directly
  const setLayerDate = (layerType: 'yield' | 'vhi' | 'lst', date: string) => {
    const dates = dateArrays[layerType];
    const index = dates.indexOf(date);
    if (index !== -1) {
      setLayerDateByIndex(layerType, index);
    }
  };

  // Toggle play/pause for a layer
  const toggleLayerPlayback = (layerType: 'yield' | 'vhi' | 'lst') => {
    setLayerStates(prev => {
      const currentState = prev[layerType];
      // Safety check: ensure currentState exists
      if (!currentState) {
        console.warn(`Layer state for ${layerType} is undefined`);
        return prev;
      }
      
      const newPlayingState = !currentState.isPlaying;

      // Handle interval management based on new state
      if (newPlayingState) {
        // Start playing - clear any existing interval first
        if (intervalsRef.current[layerType]) {
          clearInterval(intervalsRef.current[layerType]);
        }
        
        intervalsRef.current[layerType] = setInterval(() => {
          setLayerStates(prevState => {
            const dates = dateArrays[layerType];
            if (!dates || dates.length === 0) return prevState;
            
            const layerState = prevState[layerType];
            if (!layerState) return prevState;
            
            const currentIndex = layerState.currentIndex ?? 0;
            const nextIndex = (currentIndex + 1) % dates.length;
            const nextDate = dates[nextIndex];

            if (onDateChange && nextDate) {
              onDateChange(nextDate, layerType);
            }

            return {
              ...prevState,
              [layerType]: {
                ...layerState,
                currentIndex: nextIndex,
                currentDate: nextDate,
              }
            };
          });
        }, playbackSpeed);
      } else {
        // Stop playing
        if (intervalsRef.current[layerType]) {
          clearInterval(intervalsRef.current[layerType]);
          intervalsRef.current[layerType] = undefined;
        }
      }

      return {
        ...prev,
        [layerType]: {
          ...prev[layerType],
          isPlaying: newPlayingState,
        }
      };
    });
  };

  // Get currently visible layers
  const getVisibleLayers = (): Array<'yield' | 'vhi' | 'lst'> => {
    return (['yield', 'vhi', 'lst'] as const).filter(
      layer => layerStates[layer].visible
    );
  };

  // Get currently playing layers
  const getPlayingLayers = (): Array<'yield' | 'vhi' | 'lst'> => {
    return (['yield', 'vhi', 'lst'] as const).filter(
      layer => layerStates[layer].isPlaying
    );
  };

  // Stop all playback
  const stopAllPlayback = () => {
    Object.keys(intervalsRef.current).forEach(layerType => {
      const interval = intervalsRef.current[layerType as keyof typeof intervalsRef.current];
      if (interval) {
        clearInterval(interval);
        intervalsRef.current[layerType as keyof typeof intervalsRef.current] = undefined;
      }
    });

    setLayerStates(prev => ({
      yield: { ...prev.yield, isPlaying: false },
      vhi: { ...prev.vhi, isPlaying: false },
      lst: { ...prev.lst, isPlaying: false },
    }));
  };

  // Hide all layers
  const hideAllLayers = () => {
    stopAllPlayback();
    setLayerStates(prev => ({
      yield: { ...prev.yield, visible: false },
      vhi: { ...prev.vhi, visible: false },
      lst: { ...prev.lst, visible: false },
    }));
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(intervalsRef.current).forEach(interval => {
        if (interval) clearInterval(interval);
      });
    };
  }, []);

  // Update states when date arrays change
  useEffect(() => {
    setLayerStates(prev => ({
      yield: {
        ...prev.yield,
        currentDate: dateArrays.yield[0] || prev.yield.currentDate,
        currentIndex: 0,
        isPlaying: false,
      },
      vhi: {
        ...prev.vhi,
        currentDate: dateArrays.vhi[0] || prev.vhi.currentDate,
        currentIndex: 0,
        isPlaying: false,
      },
      lst: {
        ...prev.lst,
        currentDate: dateArrays.lst[0] || prev.lst.currentDate,
        currentIndex: 0,
        isPlaying: false,
      },
    }));

    // Clear all intervals when date arrays change
    stopAllPlayback();
  }, [dateArrays]);

  return {
    layerStates,
    toggleLayerVisibility,
    resetLayerState,
    setLayerDateByIndex,
    setLayerDate,
    toggleLayerPlayback,
    stopAllPlayback,
    hideAllLayers,
    getVisibleLayers,
    getPlayingLayers,
    dateArrays,
  };
}